// packages/main-app/src/utils/request.ts
import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import {
  createUnauthorizedError,
  getApiErrorMessage,
  resolveApiErrorToastMessage,
  UNAUTHORIZED_ERROR_CODE,
  isUnauthorizedError,
} from "@eweadn-qiankun/shared-utils";
import { getEnvConfig } from "../config/env";
import { useGlobalStore } from "../stores/globalStore";
import { showMessage } from "@eweadn-qiankun/shared-components";

export {
  createUnauthorizedError,
  getApiErrorMessage,
  isUnauthorizedError,
  resolveApiErrorToastMessage,
  UNAUTHORIZED_ERROR_CODE,
};

/** 后端常见错误响应结构 */
type ApiErrorBody = {
  success?: boolean;
  message?: string;
  status?: number;
  statusCode?: number;
};

/** 从 HTTP 错误响应体解析后端 message */
function extractResponseErrorMessage(
  response: AxiosResponse | undefined
): string | null {
  const body = response?.data;
  if (typeof body === "string" && body.trim()) {
    return body.trim();
  }
  if (body && typeof body === "object") {
    const message = (body as ApiErrorBody).message;
    if (typeof message === "string" && message.trim()) {
      return message.trim();
    }
  }
  return null;
}

/** 判断是否为未登录或登录已过期 */
function isUnauthorized(
  body: ApiErrorBody | undefined,
  httpStatus?: number
): boolean {
  return (
    httpStatus === 401 || body?.statusCode === 401 || body?.status === 40101
  );
}

/** 统一处理鉴权失效：清登录态并弹出登录弹窗 */
function handleUnauthorized(message?: unknown): Promise<never> {
  const errorMessage = getApiErrorMessage(message, "登录已过期，请重新登录");
  console.error(errorMessage);

  if (typeof window !== "undefined") {
    showMessage({ message: errorMessage, type: "warning" });
  }

  useGlobalStore.getState().logout();

  if (typeof window !== "undefined") {
    (window as Window & { openLoginModal?: () => void }).openLoginModal?.();
  }

  return Promise.reject(createUnauthorizedError(errorMessage));
}

// 创建 axios 实例
const service = axios.create({
  baseURL: "/api", // 使用代理，不需要完整 URL
  timeout: 60000, // 请求超时时间，60秒
  headers: {
    "Content-Type": "application/json",
  },
});

// Request 拦截器
service.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { isDevelopment } = getEnvConfig();
    const isLocalhost =
      typeof window !== "undefined" &&
      ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);

    // 仅在本地开发/本地访问时走同源代理，避免 OSS/CMS 资源的 CORS 限制
    if (
      config.headers?.["OSS-Proxy"] === "true" &&
      (isDevelopment || isLocalhost)
    ) {
      config.baseURL = "/api-file";
      delete config.headers["OSS-Proxy"];
    }
    // 从 localStorage 获取 token
    const token = localStorage.getItem("token");

    if (token && config.headers) {
      // 让每个请求携带 token
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    // FormData 须由浏览器/axios 自动带 boundary，不能沿用实例默认 application/json
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      if (config.headers) {
        delete config.headers["Content-Type"];
        delete config.headers["content-type"];
      }
    }

    return config;
  },
  (error: AxiosError) => {
    console.error("[Request Error]", error);
    return Promise.reject(error);
  }
);

// Response 拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    const res = response.data;
    const body =
      res && typeof res === "object" ? (res as ApiErrorBody) : undefined;

    if (isUnauthorized(body, response.status)) {
      return handleUnauthorized(body?.message);
    }

    if (body?.success === false) {
      return Promise.reject(new Error(body.message || "请求失败"));
    }

    // 根据后端返回的状态码判断
    if (response.status === 200 || body?.status === 200) {
      return res;
    }

    // 处理业务错误
    if (body?.status === 40001) {
      console.error("账户或密码错误！");
      return Promise.reject(new Error(body.message || "账户或密码错误！"));
    }

    // 其他错误
    console.error(body?.message || "Error");
    return Promise.reject(new Error(body?.message || "Error"));
  },
  (error: AxiosError<any>) => {
    // 处理 HTTP 状态码错误
    const { response } = error;
    const silentError = (error.config as { silentError?: boolean } | undefined)
      ?.silentError;

    if (
      isUnauthorized(
        response?.data as ApiErrorBody | undefined,
        response?.status
      )
    ) {
      return handleUnauthorized(
        (response?.data as ApiErrorBody | undefined)?.message
      );
    }

    if (response?.status === 403) {
      console.error("没有权限访问");
      return Promise.reject(new Error("没有权限访问"));
    }

    if (response?.status === 404) {
      console.error("请求资源不存在");
      return Promise.reject(new Error("请求资源不存在"));
    }

    if (response?.status === 413) {
      console.error("请求体过大");
      const body = response.data;
      const isHtml =
        typeof body === "string" && body.includes("Request Entity Too Large");
      let message = "上传文件过大，请换一张较小的图片";
      if (isHtml) {
        message =
          "上传文件过大，请换一张较小的图片（已自动压缩仍失败时，请联系管理员调大 Nginx 上传限制）";
      } else if (
        body &&
        typeof body === "object" &&
        "message" in body &&
        typeof (body as { message?: string }).message === "string"
      ) {
        message = (body as { message: string }).message;
      }
      return Promise.reject(new Error(message));
    }

    if (response?.status === 500) {
      const apiMessage = extractResponseErrorMessage(response);
      console.error(apiMessage || "服务器错误");
      return Promise.reject(new Error(apiMessage || "服务器错误"));
    }

    if (!response && error.code === "ERR_NETWORK") {
      return Promise.reject(new Error("网络异常，请检查网络后重试"));
    }

    const apiMessage = extractResponseErrorMessage(response);
    if (apiMessage) {
      return Promise.reject(new Error(apiMessage));
    }

    const fallback = error instanceof Error ? error.message : "网络错误";
    if (!silentError) {
      console.error(fallback || "网络错误");
    }
    return Promise.reject(
      error instanceof Error ? error : new Error(fallback || "网络错误")
    );
  }
);

export default service;
